/* Charmies cart engine.
 * Source of truth = Shopify cart (AJAX Cart API). localStorage 'charmies_cart_v3'
 * is a WRITE-ONLY mirror for back-compat. Reproduces the Lovable behaviour:
 *   - Unified bundle-variant billing: one charmies-bundle tier line per add,
 *     pieces carried as line-item properties.
 *   - Exact checkout note written to cart.note (Products / Price / Delivery).
 *   - Toast on add; free-shipping progress bar; piece-count badge.
 * Hardened per the M2 red-team: _uid anti-merge property, mutation mutex,
 * JSON bodies (preserve Arabic-Indic keys), numeric Item-N sort,
 * original_total_price for note math, Section Rendering for the cart page.
 */
(function () {
  'use strict';

  var C = window.CHARMIES || {};
  var LOCALE = C.locale === 'ar' ? 'ar' : 'en';
  var ROUTES = C.routes || {};
  var THRESHOLD = Number(C.freeShippingThreshold != null ? C.freeShippingThreshold : 200);
  var SHIP_FEE = Number(C.shippingFee != null ? C.shippingFee : 30);

  // ── helpers ────────────────────────────────────────────────────────────────
  var AR_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  function arabicNum(n) { return String(n).split('').map(function (d) { return AR_DIGITS[+d] != null ? AR_DIGITS[+d] : d; }).join(''); }

  function uid() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
    return 'c' + Date.now().toString(16) + Math.random().toString(16).slice(2, 10);
  }

  function t(key) {
    var en = { added_to_cart: 'Added to cart', view_cart: 'View cart', item_one: 'item', item_many: 'items' };
    var ar = { added_to_cart: 'أُضيفت إلى السلّة', view_cart: 'عرض السلة', item_one: 'قطعة', item_many: 'قطعة' };
    var b = window.charmiesT, src = en[key];
    if (b && src != null && b[src] != null) return b[src];
    return (LOCALE === 'ar' ? ar : en)[key];
  }

  // Display money — matches Lovable formatPrice (EN "QAR 99.00", AR "99.00 QAR").
  function money(major) {
    var n;
    try { n = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(major); }
    catch (e) { n = Number(major).toFixed(2); }
    return LOCALE === 'ar' ? n + ' QAR' : 'QAR ' + n;
  }
  // Note money — integer + literal "QAR" (fixed EN, matches Lovable note).
  function noteMoney(major) { return Math.round(major) + ' QAR'; }

  function itemProps(item) {
    // Shopify /cart.js exposes properties as an object; Liquid as array. Here: object.
    return item.properties || {};
  }

  // Pieces in a line: count of "Item N" props (bundle) else the line quantity (simple).
  function linePieces(item) {
    var props = itemProps(item), n = 0;
    for (var k in props) { if (/^Item \d+$/.test(k)) n++; }
    return n > 0 ? n * item.quantity : item.quantity;
  }
  function cartPieces(cart) {
    return (cart.items || []).reduce(function (s, it) { return s + linePieces(it); }, 0);
  }

  // ── checkout note (the §7 contract) ─────────────────────────────────────────
  function buildNote(cart) {
    var slots = [];
    (cart.items || []).forEach(function (item) {
      var props = itemProps(item);
      var itemKeys = Object.keys(props).filter(function (k) { return /^Item \d+$/.test(k); });
      if (itemKeys.length) {
        itemKeys.sort(function (a, b) { return parseInt(a.slice(5), 10) - parseInt(b.slice(5), 10); });
        itemKeys.forEach(function (k) { slots.push(item.quantity + 'x ' + props[k]); });
      } else {
        var enTitle = props['_TitleEn'] || item.product_title;
        slots.push(item.quantity + 'x ' + enTitle);
      }
    });
    if (!slots.length) return null; // guard: never wipe the note
    var subtotal = (cart.original_total_price || 0) / 100;
    var discount = (cart.total_discount || 0) / 100;
    var payable = subtotal - discount;
    var delivery = subtotal >= THRESHOLD ? 'Free' : noteMoney(SHIP_FEE);
    var lines = ['Products: ' + slots.join(' / ')];
    if (discount > 0) {
      // A discount is applied — spell out the math so the COD amount to collect is unambiguous.
      lines.push('Subtotal: ' + noteMoney(subtotal));
      lines.push('Discount: -' + noteMoney(discount));
      lines.push('Price: ' + noteMoney(payable));
    } else {
      lines.push('Price: ' + noteMoney(subtotal));
    }
    lines.push('Delivery: ' + delivery);
    return lines.join('\n');
  }

  // ── AJAX (JSON bodies — never FormData, to preserve Arabic-Indic keys) ───────
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body || {})
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) { var e = new Error(data && (data.description || data.message) || ('HTTP ' + r.status)); e.data = data; e.status = r.status; throw e; }
        return data;
      });
    });
  }
  function getCart() { return fetch(ROUTES.cart + '.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }); }

  // Serialize every mutation so note writes never race.
  var queue = Promise.resolve();
  function enqueue(task) {
    var run = queue.then(task, task);
    queue = run.catch(function () {}); // keep the chain alive on failure
    return run;
  }

  function mirror(cart) {
    try { localStorage.removeItem('charmies_cart_v2'); localStorage.setItem('charmies_cart_v3', JSON.stringify({ v: 3, item_count: cart.item_count, items: cart.items })); } catch (e) {}
  }

  // After any mutation: read canonical cart, write note, sync UI.
  function settle() {
    return getCart().then(function (cart) {
      var note = buildNote(cart);
      var p = note != null ? postJSON(ROUTES.cartUpdate, { note: note }) : Promise.resolve(cart);
      return p.then(function (after) {
        var finalCart = (after && after.items) ? after : cart;
        mirror(finalCart);
        syncUI(finalCart);
        return finalCart;
      });
    });
  }

  // ── UI sync ──────────────────────────────────────────────────────────────
  function syncUI(cart) {
    var pieces = cartPieces(cart);
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = pieces; // raw digits in both locales, matching Lovable + the cart-page H1
      el.classList.toggle('hidden', pieces === 0);
    });
    updateFreeShipBar(cart);
    renderDiscount(cart);
  }

  // Show the applied discount inside the cart (so shoppers see their 5% BEFORE checkout, not only
  // on the checkout page). Prefers the live cart's applicable code; falls back to the code we stored
  // when it was applied.
  function renderDiscount(cart) {
    var hasDiscount = (cart.total_discount || 0) > 0;
    var code = '';
    var codes = (cart.discount_codes || []).filter(function (d) { return d && d.applicable; });
    if (codes.length) { code = codes[0].code; }
    else { try { code = localStorage.getItem('charmies_promo_code') || ''; } catch (e) {} }
    var active = hasDiscount && cart.item_count > 0;

    // (1) Top celebratory chip — confirms a code is on the order. No amount here; the breakdown owns the number.
    var banner = document.querySelector('[data-cart-discount]');
    if (banner) {
      if (active && code) {
        banner.className = 'mb-4 flex items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm font-semibold text-emerald-700';
        banner.innerHTML = '<span>🎉 ' + esc(LOCALE === 'ar' ? 'تم تطبيق خصمك' : 'Your discount is applied') + '</span><span class="shrink-0 rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-bold text-white">' + esc(code) + '</span>';
      } else { banner.className = 'hidden'; banner.innerHTML = ''; }
    }

    // (2) Discount line in the summary breakdown (Subtotal − Discount = Total).
    var row = document.querySelector('[data-cart-discount-row]');
    var amtEl = document.querySelector('[data-cart-discount-amt]');
    if (row && amtEl) {
      if (active) { amtEl.textContent = '−' + money(cart.total_discount / 100); row.classList.remove('hidden'); }
      else { row.classList.add('hidden'); amtEl.textContent = ''; }
    }

    // (3) The displayed Total MUST reflect the discount (it was showing the pre-discount amount, so
    // the banner promised −13.85 while the total stayed 277 — looked broken). Update every total node.
    var payable = (cart.original_total_price || 0) - (active ? (cart.total_discount || 0) : 0);
    document.querySelectorAll('[data-cart-total]').forEach(function (el) { el.textContent = money(payable / 100); });
  }

  function updateFreeShipBar(cart) {
    var bar = document.querySelector('[data-freeship]');
    if (!bar) return;
    var subtotal = (cart.original_total_price || 0) / 100;
    var qualifies = subtotal >= THRESHOLD;
    var remaining = Math.max(0, THRESHOLD - subtotal);
    var pct = Math.min(100, Math.round((subtotal / THRESHOLD) * 100));
    var fill = bar.querySelector('[data-freeship-fill]');
    var text = bar.querySelector('[data-freeship-text]');
    if (fill) { fill.style.width = pct + '%'; fill.classList.toggle('bg-emerald-500', qualifies); fill.classList.toggle('bg-rose-deep', !qualifies); }
    if (text) {
      var tb = window.charmiesT;
      if (qualifies) {
        text.textContent = (tb && tb["You've unlocked free delivery!"]) || (LOCALE === 'ar' ? 'حصلتِ على توصيل مجاني!' : "You've unlocked free delivery!");
      } else {
        var tpl = (tb && tb.free_shipping_progress) || (LOCALE === 'ar' ? 'أضيفي {amount} للحصول على توصيل مجاني' : 'Add {amount} more for free delivery');
        text.textContent = tpl.replace('{amount}', money(remaining));
      }
    }
  }

  // ── toast ────────────────────────────────────────────────────────────────
  function toastContainer() {
    var el = document.getElementById('charmies-toasts');
    if (!el) {
      el = document.createElement('div');
      el.id = 'charmies-toasts';
      el.setAttribute('aria-live', 'polite');
      el.className = 'pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 px-4 pb-[calc(env(safe-area-inset-bottom,0px)+5.5rem)]';
      document.body.appendChild(el);
    }
    return el;
  }
  function showToast(o) {
    var dir = LOCALE === 'ar' ? 'rtl' : 'ltr';
    var unit = o.pieces === 1 ? t('item_one') : t('item_many');
    var card = document.createElement('div');
    card.dir = dir;
    card.className = 'pointer-events-auto w-[min(92vw,360px)] overflow-hidden rounded-2xl border border-border bg-card shadow-xl';
    card.innerHTML =
      '<div class="flex items-center gap-2 bg-emerald-50 px-4 py-2 text-emerald-700">' +
        '<span class="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white">✓</span>' +
        '<span class="text-xs font-semibold tracking-wide">' + esc(t('added_to_cart')) + '</span>' +
      '</div>' +
      '<div class="flex gap-3 p-3">' +
        (o.image ? '<img src="' + esc(o.image) + '" alt="" class="h-16 w-16 flex-shrink-0 rounded-xl object-cover">' : '') +
        '<div class="min-w-0 flex-1">' +
          '<p class="truncate text-sm font-semibold text-primary">' + esc(o.title || '') + '</p>' +
          (o.label ? '<p class="truncate text-[11px] text-muted-foreground">' + esc(o.label) + (o.pieces ? ' · ' + o.pieces + ' ' + unit : '') + '</p>' : '') +
          '<div class="mt-1.5 text-sm font-bold text-primary">' + esc(money(o.price)) + '</div>' +
        '</div>' +
      '</div>' +
      '<button type="button" class="flex w-full items-center justify-center gap-1.5 border-t border-border bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90">' + esc(t('view_cart')) + '</button>';
    card.querySelector('button').addEventListener('click', function () { window.location.href = ROUTES.cart; });
    toastContainer().appendChild(card);
    var timer = setTimeout(remove, 4500);
    card.addEventListener('mouseenter', function () { clearTimeout(timer); });
    card.addEventListener('mouseleave', function () { timer = setTimeout(remove, 1500); });
    function remove() { card.style.transition = 'opacity .25s'; card.style.opacity = '0'; setTimeout(function () { card.remove(); }, 250); }
  }
  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  // Non-blocking error toast (mirrors Lovable's toast.error). No success styling/CTA.
  function showError(msg) {
    var card = document.createElement('div');
    card.dir = LOCALE === 'ar' ? 'rtl' : 'ltr';
    card.className = 'pointer-events-auto w-[min(92vw,360px)] rounded-2xl border border-destructive/40 bg-card px-4 py-3 text-sm font-medium text-destructive shadow-xl';
    card.setAttribute('role', 'alert');
    card.textContent = msg;
    toastContainer().appendChild(card);
    setTimeout(function () { card.style.transition = 'opacity .25s'; card.style.opacity = '0'; setTimeout(function () { card.remove(); }, 250); }, 3500);
  }

  // ── public API ───────────────────────────────────────────────────────────
  // line = { tierVariantId, bundleLabelEn, bundleLabelAr, totalPrice, nameEn, nameAr, image,
  //          slots: [{ typeEn,typeAr, colorEn,colorAr, scentEn,scentAr, image, variantId }] }
  function addBundle(line, opts) {
    var express = opts && opts.express;
    var ready, fail, added = new Promise(function (res, rej) { ready = res; fail = rej; });
    var run = enqueue(function () {
      var props = { Bundle: line.bundleLabelEn };
      line.slots.forEach(function (s, i) {
        props['Item ' + (i + 1)] = [s.typeEn, s.colorEn, s.scentEn].filter(Boolean).join(' · ');
        props['العنصر ' + arabicNum(i + 1)] = [s.typeAr, s.colorAr, s.scentAr].filter(Boolean).join(' · ');
      });
      props['_uid'] = uid(); // anti-merge: every bundle add is its own line
      props['_editUrl'] = (window.location && window.location.pathname) || ''; // the "Edit" link on the cart line returns here
      if (line.image) props['_image'] = line.image;
      // Inventory sync: for bundle/tier lines (billed to the phantom pricing variant), record the
      // REAL charm variant ids so an order automation can decrement single-charm stock. Single
      // buys bill their own variant (Shopify decrements them already) so they're skipped here.
      var billedId = idOf(line.tierVariantId);
      var isBundleLine = line.slots.length && line.slots.every(function (s) { return idOf(s.variantId) !== billedId; });
      if (isBundleLine) { props['_charms'] = line.slots.map(function (s) { return idOf(s.variantId); }).join(','); } // show the chosen charm on the cart line, not the generic bundle photo
      if (line.extraProps) { Object.keys(line.extraProps).forEach(function (k) { var v = line.extraProps[k]; if (v != null && v !== '') props[k] = v; }); } // package lines add extra properties (phone model, "+ Mirror", …)
      return postJSON(ROUTES.cartAdd, { id: idOf(line.tierVariantId), quantity: 1, properties: props })
        .then(function () {
          // Item is IN the cart — give instant feedback and settle (re-read + note write) in the
          // background, still serialized on the queue so a fast second click can't race the note.
          firePixelAdd(line);
          if (!express) showToast({ image: line.image, title: LOCALE === 'ar' ? line.nameAr : line.nameEn, label: LOCALE === 'ar' ? line.bundleLabelAr : line.bundleLabelEn, pieces: line.slots.length, price: line.totalPrice });
          ready();
          return settle().catch(function () { return getCart().catch(function () { return null; }); }); // a note/refresh blip must not read as "add failed"
        }, function (e) { fail(e); throw e; });
    });
    // Express (Buy-it-now / Apple Pay) needs the settled cart before navigating; a normal add returns
    // as soon as the item lands so the button releases instantly.
    return express ? run : added;
  }

  // line = { variantId, quantity, titleEn, nameEn, nameAr, image, totalPrice }
  function addSimple(line, opts) {
    var express = opts && opts.express;
    var ready, fail, added = new Promise(function (res, rej) { ready = res; fail = rej; });
    var run = enqueue(function () {
      var props = { _TitleEn: line.titleEn, _editUrl: (window.location && window.location.pathname) || '' };
      return postJSON(ROUTES.cartAdd, { id: idOf(line.variantId), quantity: line.quantity || 1, properties: props })
        .then(function () {
          // The item is IN the cart. Give instant feedback (toast + release the button) and let the
          // cart re-read + note write finish in the BACKGROUND — still serialized on the queue, so a
          // fast second click can't race the note write.
          firePixelAdd({ totalPrice: line.totalPrice, nameEn: line.nameEn, slots: [{ variantId: line.variantId }] });
          if (!express) showToast({ image: line.image, title: LOCALE === 'ar' ? line.nameAr : line.nameEn, label: '', pieces: line.quantity || 1, price: line.totalPrice });
          ready();
          return settle().catch(function () { return getCart().catch(function () { return null; }); }); // a note/refresh blip must not read as "add failed"
        }, function (e) { fail(e); throw e; });
    });
    // Express (Buy-it-now / Apple Pay) needs the fully-settled cart before it navigates to checkout;
    // a normal Add-to-Cart returns as soon as the item lands so the UI feels instant.
    return express ? run : added;
  }

  // Remove / change a line on the cart page (by line KEY), re-render via Section Rendering.
  function changeLine(key, quantity, sectionId) {
    return enqueue(function () {
      var body = { id: key, quantity: quantity };
      if (sectionId) { body.sections = sectionId; body.sections_url = window.location.pathname; }
      return postJSON(ROUTES.cartChange, body).then(function (res) {
        if (sectionId && res.sections && res.sections[sectionId]) renderSection(sectionId, res.sections[sectionId]);
        return settle();
      });
    });
  }

  // Black Apple Pay skin (see main-cart.liquid): the inline <script> that switches it on only runs on
  // first page load. A Section-Rendering re-render swaps innerHTML and drops it — which is why the
  // button reverted to Shopify's raw white one after any qty/edit action. Re-assert it after render.
  // Gated on the device actually supporting Apple Pay, so Android/desktop never get the wrong skin.
  var _applePayCap = null;
  function applePayAvailable() {
    if (_applePayCap === null) {
      try { _applePayCap = !!(window.ApplePaySession && ApplePaySession.canMakePayments && ApplePaySession.canMakePayments()); }
      catch (e) { _applePayCap = false; }
    }
    return _applePayCap;
  }
  function applyApplePaySkin() {
    if (!applePayAvailable()) return;
    var list = document.querySelectorAll('[data-applepay-wrap]');
    for (var i = 0; i < list.length; i++) {
      if (!list[i].hasAttribute('data-applepay-on')) list[i].setAttribute('data-applepay-on', '');
    }
  }

  function renderSection(id, html) {
    var target = document.getElementById('shopify-section-' + id);
    if (!target) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.getElementById('shopify-section-' + id);
    target.innerHTML = fresh ? fresh.innerHTML : html;
    applyApplePaySkin();
  }

  function idOf(v) { // accept numeric id or GID
    var s = String(v);
    var m = s.match(/(\d+)\s*$/);
    return m ? m[1] : s;
  }

  function firePixelAdd(line) {
    if (!window.CharmiesPixels) return;
    var slots = line.slots || [];
    var per = slots.length ? line.totalPrice / slots.length : line.totalPrice;
    window.CharmiesPixels.addToCart({
      contentName: line.nameEn,
      items: slots.map(function (s) { return { id: idOf(s.variantId), quantity: 1, price: per, name: line.nameEn }; }),
      value: line.totalPrice
    });
  }

  // checkout: ensure note is settled, then go to /checkout (whole cart).
  // preCart (from a just-completed addBundle/addSimple) lets the express Buy-it-now / Apple Pay
  // path SKIP a redundant second settle — the note was already written by the add. This cuts
  // express checkout from 5 sequential cart calls (add, get, note, get, note) down to 3, which
  // is the "crazy slow" the owner reported. Standalone callers (cart page) still settle once.
  function checkout(preCart) {
    var task = (preCart && preCart.items) ? function () { return preCart; } : function () { return settle().catch(function () { return getCart().catch(function () { return null; }); }); };
    return enqueue(task).then(function (cart) {
      if (window.CharmiesPixels && cart) {
        var items = [];
        (cart.items || []).forEach(function (it) {
          var props = itemProps(it);
          var keys = Object.keys(props).filter(function (k) { return /^Item \d+$/.test(k); });
          if (keys.length) keys.forEach(function () { items.push({ id: String(it.variant_id), quantity: 1, price: it.price / 100, name: it.product_title }); });
          else items.push({ id: String(it.variant_id), quantity: it.quantity, price: it.price / 100, name: it.product_title });
        });
        window.CharmiesPixels.initiateCheckout({ items: items, value: (cart.original_total_price || 0) / 100 });
      }
      window.location.href = '/checkout';
    });
  }

  // Re-assert the checkout note (Lovable built the note AT checkout time). Used by the cart-page checkout submit.
  function settleNote() { return enqueue(settle); }

  // Customer note → a cart ATTRIBUTE ("Customer note"), kept separate from cart.note (the
  // auto-generated driver summary), so the §7 note contract is never touched. Shows on the order.
  function setNote(text) {
    return enqueue(function () {
      return postJSON(ROUTES.cartUpdate, { attributes: { 'Customer note': (text == null ? '' : String(text)) } });
    });
  }

  // Lightweight success toast (separate from the add-to-cart card) — confirms the 5% code.
  function notify(msg) {
    var card = document.createElement('div');
    card.dir = LOCALE === 'ar' ? 'rtl' : 'ltr';
    card.className = 'pointer-events-auto w-[min(92vw,360px)] rounded-2xl border border-emerald-300 bg-white px-4 py-3 text-center text-sm font-bold text-emerald-700 shadow-xl';
    card.setAttribute('role', 'status');
    card.textContent = msg;
    toastContainer().appendChild(card);
    setTimeout(function () { card.style.transition = 'opacity .3s'; card.style.opacity = '0'; setTimeout(function () { card.remove(); }, 300); }, 3800);
  }

  // Apply a discount code WITHOUT navigating away — a reload would wipe an in-progress bundle build
  // (the builder is in-memory JS state). Shopify's /discount/CODE endpoint sets the discount cookie;
  // we hit it in the background (redirecting to the lightweight /cart.js), then refresh the cart UI
  // so the code shows immediately. localStorage keeps it visible even before the cart has items.
  function applyDiscount(code) {
    code = code || 'NEW5';
    try { localStorage.setItem('charmies_promo_code', code); } catch (e) {}
    return fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', { credentials: 'same-origin' })
      .then(function () { return getCart(); })
      .then(function (cart) { mirror(cart); syncUI(cart); return cart; })
      .catch(function () { return null; });
  }

  window.CharmiesCart = { addBundle: addBundle, addSimple: addSimple, changeLine: changeLine, checkout: checkout, settleNote: settleNote, setNote: setNote, getCart: getCart, refresh: function () { return getCart().then(function (c) { mirror(c); syncUI(c); return c; }); }, money: money, cartPieces: cartPieces, toastError: showError, notify: notify, applyDiscount: applyDiscount };

  // Customer note field on the cart page. Delegated so it survives Section-Rendering re-renders.
  document.addEventListener('change', function (e) {
    var n = e.target && e.target.closest && e.target.closest('[data-cart-note]');
    if (!n) return;
    setNote(n.value).then(function () {
      var s = n.parentNode && n.parentNode.querySelector('[data-cart-note-status]');
      if (s) { s.textContent = (window.charmiesT && window.charmiesT['Saved ✓']) || (LOCALE === 'ar' ? 'تم الحفظ ✓' : 'Saved ✓'); setTimeout(function () { s.textContent = ''; }, 2000); }
    });
  });

  // Announcement bar (or any /discount/CODE link): apply the code IN THE BACKGROUND (no reload), so
  // clicking it never resets an in-progress bundle build. If the builder is on this page, stay put and
  // just confirm with a toast; otherwise send them to the builder afterwards (discount already applied).
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('a[href*="/discount/"]');
    if (!a) return;
    e.preventDefault();
    var m = (a.getAttribute('href') || '').match(/\/discount\/([^/?#]+)/);
    var code = m ? decodeURIComponent(m[1]) : 'NEW5';
    var msg = LOCALE === 'ar' ? '🎉 تم تفعيل خصم ٥٪ — يظهر عند الدفع' : '🎉 Your 5% is applied — shows at checkout';
    applyDiscount(code).then(function () {
      if (document.getElementById('charmies-bundle-builder')) {
        notify(msg); // builder is on this page — never reload/reset it
      } else {
        try { sessionStorage.setItem('charmies_promo_toast', '1'); } catch (e2) {}
        window.location.href = '/products/charmies-bundle';
      }
    });
  });

  document.addEventListener('DOMContentLoaded', function () {
    try { localStorage.removeItem('charmies_cart_v2'); } catch (e) {}
    window.CharmiesCart.refresh();
    applyApplePaySkin();
    try {
      if (sessionStorage.getItem('charmies_promo_toast')) {
        sessionStorage.removeItem('charmies_promo_toast');
        try { localStorage.setItem('charmies_promo_code', 'NEW5'); } catch (e4) {} // so the cart shows it too
        notify(LOCALE === 'ar' ? '🎉 تم تفعيل خصم ٥٪ — يظهر عند الدفع' : '🎉 Your 5% is applied — shows at checkout');
      }
    } catch (e3) {}
  });
})();
